/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useState, useMemo } from 'react';
import { Button } from 'reactstrap';
import UniversalTrackerValue from '../../model/UniversalTrackerValue';
import { formatLongDate } from '../../utils/date';
import { UTR } from '../../constants/labels';
import { UtrvStatus } from '../../constants/status';
import { EvidenceModal } from '@features/files/EvidenceModal';
import { ValueHistoryText } from './ValueHistoryText';
import { setCurrentHistoryData } from '../../actions/api';
import { Loader } from '@g17eco/atoms/loader';
import config from '../../config';
import { UtrvAssuranceStatus, ValueHistory, SourceItem } from '../../types/universalTrackerValue';
import { HistoryDocument } from '../../types/document';
import { UserMin } from '@g17eco/types/user';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import './history-timeline.scss';
import { sortByDateAndId } from '@utils/sort';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { UnitConfig } from '@g17eco/types/survey';
import { isAggregatedSurvey } from '@utils/survey';
import type { IntegrationProvider } from '@g17eco/types/app-integration';
import { normalizeEvidence } from '@utils/survey/evidence';
import {
  getHistorySource,
  isSourceDataTransfer,
  isSourceExternalConnection,
  isSourceSecondaryConnection,
  isUtrvAssuranceRejected,
  isUtrvAssuranceRestated,
} from '@utils/universalTrackerValue';
import UniversalTrackerClassInterface from '../../model/UniversalTrackerClassInterface';
import { useUtrValueList } from '@hooks/useUtrValueList';

interface Props {
  universalTracker: UniversalTrackerClassInterface;
  history: ValueHistory[];
  providers?: Array<Pick<IntegrationProvider, 'code' | 'name' | 'logo' | 'icon'>>;
  documents: HistoryDocument[];
  users: UserMin[];
  surveyType?: SurveyType;
  isComposite?: boolean;
  isStakeholder?: boolean;
  displayEvidence?: boolean;
  displayNote?: boolean;
  isFragment?: boolean;
  sourceItems?: SourceItem[];
  reload?: () => void;
  utrvId?: string;
  isReadOnly?: boolean;
  unitConfig?: UnitConfig;
  initiativeId: string;
}

export const HistoryTimelineComponent: React.FC<Props> = ({
  universalTracker,
  history: propHistory,
  providers,
  documents,
  users,
  surveyType,
  isComposite,
  isStakeholder = false,
  displayEvidence = true,
  displayNote = true,
  isFragment = false,
  sourceItems,
  reload,
  utrvId,
  isReadOnly = false,
  unitConfig,
  initiativeId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [displaySupportingDocuments, setDisplaySupportingDocuments] = useState(false);
  const [displayLocationInfo, setDisplayLocationInfo] = useState<boolean | undefined | number>(false);
  const [lastHistory, setLastHistory] = useState<ValueHistory | undefined>();

  const utrValueList = useUtrValueList(universalTracker.getRaw(), initiativeId);

  const userMap = useMemo(() => {
    const map = new Map();
    users.forEach((u: UserMin) => map.set(u._id, u));
    return map;
  }, [users]);

  const history = useMemo(() => {
    return [...propHistory].sort(sortByDateAndId);
  }, [propHistory]);

  const toggleSupportingDocuments = (e: React.MouseEvent, h?: ValueHistory) => {
    e.preventDefault();
    setDisplaySupportingDocuments(!displaySupportingDocuments);
    setLastHistory(h);
  };

  const toggleLocationInfo = (e: React.MouseEvent, id: number | undefined = undefined) => {
    e.preventDefault();
    setDisplayLocationInfo(id);
  };

  const triggerHistoryUpdate = (e: React.MouseEvent, h: ValueHistory) => {
    e.preventDefault();
    if (!utrvId) {
      return;
    }
    setLoading(true);

    setCurrentHistoryData(utrvId, h)
      .then(() => {
        setLoading(false);
        reload?.();
      })
      .catch((e: any) => {
        setLoading(false);
        setErrorMessage(e.message);
      });
  };

  const renderHistoryIcon = (h: ValueHistory) => {
    // Handle assurance statuses first (these take priority)
    if (h.assuranceStatus === UtrvAssuranceStatus.Restated) {
      return <i className='fal fa-rotate fs-5' />;
    }
    if (h.assuranceStatus === UtrvAssuranceStatus.Completed) {
      return <i className='fal fa-award fs-5' />;
    }
    if (h.assuranceStatus === UtrvAssuranceStatus.Partial) {
      return <i className='fal fa-award partially-assured-icon fs-5' />;
    }
    if (h.assuranceStatus === UtrvAssuranceStatus.Rejected) {
      return <i className='fal fa-award fs-5' />;
    }

    // Map action types to appropriate icons per ticket requirements
    // Colors are inherited from parent span's CSS classes
    switch (h.action) {
      case 'verified':
        return <i className='fal fa-user-check fs-5' />;
      case 'updated':
      case 'submitted':
        return <i className='fal fa-user-edit fs-5' />;
      case 'rejected':
        return <i className='fal fa-user-times fs-5' />;
      case 'disputed':
        return <i className='fal fa-award fs-5' />;
      case 'created':
        // Keep circle for created status as per requirements (no change)
        return <i className='fa fa-circle' />;
      default:
        return <i className='fa fa-circle' />;
    }
  };

  const renderLocationInfo = (historyItem: ValueHistory, i: number) => {
    if (!historyItem.location || !historyItem.location.latitude || !historyItem.location.longitude) {
      return null;
    }

    if (displayLocationInfo === i) {
      const latLong = `${historyItem.location.latitude},${historyItem.location.longitude}`;
      return (
        <div className='mapContainer'>
          <div className='d-flex justify-content-between'>
            <Button className='closeButton' color='danger' onClick={(e) => toggleLocationInfo(e, undefined)}>
              <i className='fa fas fa-times' />
            </Button>
            <a
              href={`https://maps.google.com/?q=${latLong}`}
              target='_blank'
              rel='noopener noreferrer'
              className='align-self-end'
            >
              <small>View in Google maps</small>
              <i className='fas fa-external-link-alt ml-2'></i>
            </a>
          </div>
          <img
            alt='Geo location'
            src={`https://maps.googleapis.com/maps/api/staticmap?zoom=16&key=${config.googleMapsKey}&size=600x300&markers=${latLong}`}
          />
        </div>
      );
    }

    return (
      <div className='link-like' onClick={(e) => toggleLocationInfo(e, i)}>
        <i className='fa-light fa-location-dot text-ThemeAccentMedium text-sm mr-1' />
        View location info
      </div>
    );
  };

  const getText = (statusText: string, className: string, h: ValueHistory) => {
    const userName = getName(h.userId);

    if (isComposite) {
      const text = ValueHistoryText.getCompositeHistoryText(className, statusText, h, universalTracker, userName);

      const evidenceData = normalizeEvidence(h);
      const showEvidence = displayEvidence && evidenceData.length > 0;
      const showNote = displayNote && h.note;
      if (text === undefined && (showEvidence || showNote)) {
        return 'Supporting information provided';
      }

      return text;
    }

    return ValueHistoryText.getHistoryText({
      className,
      status: statusText,
      history: h,
      username: userName,
      universalTracker,
      unitConfig,
      utrValueList
    });
  };

  const renderHistoryDocuments = (historyItem: ValueHistory) => {
    const evidenceData = normalizeEvidence(historyItem);
    const count = evidenceData.length;
    const text = `View ${count} supporting file${count > 1 ? 's' : ''}`;

    return (
      <div className='historyDetails'>
        <span className={'link-like'} onClick={(e) => toggleSupportingDocuments(e, historyItem)}>
          <i className='fa-light fa-file text-ThemeAccentMedium text-sm' />
          {text}
        </span>
      </div>
    );
  };

  const isDataTransferHistory = (historyItem: ValueHistory): boolean => {
    const source = getHistorySource(historyItem.valueData);
    return source !== undefined && isSourceDataTransfer(source);
  };

  const renderProviders = (historyItem: ValueHistory) => {
    const source = getHistorySource(historyItem.valueData);
    if (!source || !providers) {
      return null;
    }

    if (isSourceExternalConnection(source)) {
      const provider = providers.find((p) => p.code === source.data?.integrationCode);
      return provider ? (
        <SimpleTooltip text={`Data originates from ${provider.name} integration`}>
          <i className='fal fa-circle-nodes mr-1 text-ThemeTextMedium' />
        </SimpleTooltip>
      ) : null;
    }

    if (isSourceSecondaryConnection(source)) {
      return (
        <SimpleTooltip text='Data originates from secondary connection'>
          <i className='fal fa-diagram-nested mr-1 text-ThemeTextMedium' />
        </SimpleTooltip>
      );
    }

    if (isSourceDataTransfer(source)) {
      const provider = providers.find((p) => p.code === source.data?.integrationCode);
      return (
        <div className='d-flex align-items-center flex-wrap gap-1 mt-1 small'>
          <span className='badge rounded-pill bg-info fw-normal'>
            <i className='fal fa-arrow-right-arrow-left me-1' />Data Sync
          </span>
          {provider && (
            <span className='text-ThemeTextMedium'>
              from {provider.logo && <img src={provider.logo} alt='' style={{ height: 14, marginRight: 2, verticalAlign: 'middle' }} />}
              <strong>{provider.name}</strong>
            </span>
          )}
          {source.data?.snapshotAt && (
            <span className='text-ThemeTextLight' title={new Date(source.data.snapshotAt).toISOString()}>
              · Snapshot: {formatLongDate(source.data.snapshotAt)}
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  const getName = (userId: string) => {
    const hasAggregatedData = sourceItems && sourceItems.length > 1;

    if (hasAggregatedData || isAggregatedSurvey(surveyType)) {
      return 'aggregated combined report';
    }

    const user = userMap.get(userId);
    return user ? `${user.firstName} ${user.surname}` : '- -';
  };

  if (loading) {
    return <Loader />;
  }

  if (history.length === 0) {
    return 'No History available';
  }

  return (
    <div className='timeline-wrapper'>
      <div className='timeline'>
        {errorMessage ? <BasicAlert type={'danger'}>{errorMessage}</BasicAlert> : ''}
        {history.map((h, i) => {
          const statusText = UniversalTrackerValue.getStatusTextByHistory(h);

          const isAssuranceRestated = isUtrvAssuranceRestated(h.assuranceStatus);
          const isAssuranceRejected = isUtrvAssuranceRejected(h.assuranceStatus);
          const useAssuranceStatus = isAssuranceRestated || isAssuranceRejected;
          const className = useAssuranceStatus ? `${h.assuranceStatus}` : statusText.toLowerCase();

          const evidenceData = normalizeEvidence(h);
          const showEvidence = displayEvidence && evidenceData.length > 0;
          const showNote = displayNote && h.note;
          const text = getText(statusText, className, h);
          if (text === undefined) {
            return '';
          }
          return (
            <div className='row' key={i}>
              <div className='col-12 col-md-3'>
                <span className={`${className} mr-1`}>{renderHistoryIcon(h)}</span>
                {formatLongDate(h.date)}
                {i !== 0 &&
                isFragment &&
                h.action === 'updated' &&
                !isAssuranceRestated &&
                isStakeholder &&
                !isReadOnly ? (
                  <SimpleTooltip text={'Click to undo action'}>
                    <i
                      onClick={(e) => triggerHistoryUpdate(e, h)}
                      className={'fas fa-undo ml-2 text-ThemeAccentMedium'}
                    />
                  </SimpleTooltip>
                ) : (
                  <></>
                )}
              </div>
              <div className='col'>
                {text}
                {renderProviders(h)}
                {showEvidence && renderHistoryDocuments(h)}
                {renderLocationInfo(h, i)}
                {showNote && (
                  <div className='historyDetails'>
                    {UTR.NOTE}:&nbsp;<span className='dont_translate'>{h.note}</span>
                  </div>
                )}
                {h.valueData?.isImported === true && !isDataTransferHistory(h) && h.action !== UtrvStatus.Verified && (
                  <BasicAlert type='info'>
                    <i className='fa fa-exclamation-triangle mr-2' />
                    This data was imported from outside the platform and may not have been validated.
                  </BasicAlert>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {displayEvidence && (
        <EvidenceModal
          history={lastHistory}
          isOpen={displaySupportingDocuments}
          documents={documents}
          toggle={toggleSupportingDocuments}
        />
      )}
    </div>
  );
};
